import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Card,
  Row,
  Col,
  Select,
  DatePicker,
  Switch,
  message,
  Typography,
  Space,
  Divider,
  Alert
} from 'antd';
import {
  SaveOutlined,
  ArrowLeftOutlined,
  UserOutlined,
  SafetyOutlined
} from '@ant-design/icons';
import { adminAPI, authAPI } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import dayjs from 'dayjs';
import FingerprintCapture from '../Biometric/FingerprintCapture';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const ConsumerForm = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [centers, setCenters] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [fingerprintTemplates, setFingerprintTemplates] = useState([null, null]);

  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();

  useEffect(() => {
    if (id) {
      setIsEditing(true);
      fetchConsumer();
    }
    fetchCenters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchConsumer = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getConsumer(id);
      const consumer = response.data.consumer;

      form.setFieldsValue({
        ...consumer,
        date_of_birth: consumer.date_of_birth ? dayjs(consumer.date_of_birth) : null,
        start_date: consumer.start_date ? dayjs(consumer.start_date) : null,
      });
      setFingerprintTemplates([
        consumer.fingerprint_template_1 || null,
        consumer.fingerprint_template_2 || null
      ]);
    } catch (error) {
      message.error('Failed to fetch consumer details');
      navigate('/consumers');
    } finally {
      setLoading(false);
    }
  };

  const fetchCenters = async () => {
    try {
      let response;
      if (user?.role === 'super_admin') {
        response = await adminAPI.getWellnessCenters();
        setCenters(response.data.centers || []);
      } else {
        // For regular admins, only show their center
        response = await authAPI.getWellnessCenters();
        const allCenters = response.data.centers || [];
        const userCenter = allCenters.find(center => center.id === user.wellness_center_id);
        setCenters(userCenter ? [userCenter] : []);
      }
    } catch (error) {
      message.error('Failed to fetch wellness centers');
    }
  };

  const extractBiometricData = (xmlString) => {
    if (!xmlString) return null;
    const dataMatch = xmlString.match(/<Data[^>]*>([^<]+)<\/Data>/);
    return dataMatch ? dataMatch[1].trim() : null;
  };

  const handleFingerprintCapture = (index, xmlTemplate) => {
    const base64Data = extractBiometricData(xmlTemplate);
    if (!base64Data) {
      message.error(`Failed to extract template from Finger ${index + 1}. Please try again.`);
      return;
    }
    
    setFingerprintTemplates(prev => {
      const updated = [...prev];
      updated[index] = base64Data;
      return updated;
    });
    message.success(`Finger ${index + 1} captured and ready to save.`);
  };

  const handleSubmit = async (values) => {
    try {
      setSaving(true);

      if (isEditing) {
        // For updates, only send allowed fields + fingerprints if re-enrolled
        const formData = {
          name: values.name,
          email: values.email,
          phone: values.phone || null,
          date_of_birth: values.date_of_birth ? values.date_of_birth.format('YYYY-MM-DD') : null,
          gender: values.gender || null,
          added_via: values.added_via || null,
          address: values.address || null,
          is_active: values.is_active,
        };

        // Include fingerprints in update
        formData.fingerprint_template_1 = fingerprintTemplates[0];
        formData.fingerprint_template_2 = fingerprintTemplates[1];

        await adminAPI.updateConsumer(id, formData);
        message.success('Consumer updated successfully');
      } else {
        // For creation, include all fields
        const formData = {
          ...values,
          date_of_birth: values.date_of_birth ? values.date_of_birth.format('YYYY-MM-DD') : null,
          start_date: values.start_date.format('YYYY-MM-DD'),
          fingerprint_template_1: fingerprintTemplates[0] || null,
          fingerprint_template_2: fingerprintTemplates[1] || null,
        };

        // For regular admins, use their center ID
        if (user?.role !== 'super_admin') {
          formData.wellness_center_id = user.wellness_center_id;
        }
        const response = await adminAPI.createConsumer(formData);

        // Show success message with email notification status
        if (response.data.email_scheduled && values.email) {
          message.success(
            `Consumer created successfully! ✅ Welcome email sent to ${response.data.consumer.email}`,
            6 // Show for 6 seconds
          );

          // Show additional info in console for admin reference
          console.log('Consumer created with temp password:', response.data.temp_password);
        } else if (!values.email) {
          message.success(
            `Consumer created successfully! ✅ (No email provided for notification)`,
            8
          );
          console.log('IMPORTANT - Share these credentials with consumer manually:');
          console.log('Name:', response.data.consumer.name);
          console.log('Temporary Password:', response.data.temp_password);
        } else {
          message.success(
            `Consumer created successfully✅ check your email for the login credentials`,
            8 // Show for 8 seconds
          );

          // Show temp password in console for manual sharing
          console.log('IMPORTANT - Share these credentials with consumer manually:');
          console.log('Email:', response.data.consumer.email);
          console.log('Temporary Password:', response.data.temp_password);
        }
      }

      navigate('/consumers');
    } catch (error) {
      const errorMessage = error.response?.data?.error ||
        `Failed to ${isEditing ? 'update' : 'create'} consumer`;
      message.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/consumers');
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleCancel}
            >
              Back
            </Button>
            <Title level={2} style={{ margin: 0 }}>
              {isEditing ? 'Edit Consumer' : 'Add New Consumer'}
            </Title>
          </Space>
        </Col>
      </Row>

      <Card loading={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            is_active: true,
            start_date: dayjs(),
            gender: 'male'
          }}
        >
          <Row gutter={[24, 0]}>
            {/* Personal Information */}
            <Col span={24}>
              <div className="form-section">
                <Title level={4}>Personal Information</Title>

                {/* 🧬 Biometric Enrollment - Moved to Top for High Visibility */}
                <div style={{ 
                  background: '#f0f7ff', 
                  padding: '20px', 
                  borderRadius: '12px', 
                  border: '1px solid #bae7ff',
                  marginBottom: '24px',
                  marginTop: '10px'
                }}>
                  <Title level={4} style={{ color: '#0050b3', marginBottom: '8px' }}>
                    <SafetyOutlined style={{ marginRight: 8 }} />
                    Biometric Enrollment
                  </Title>
                  <p style={{ fontSize: '14px', color: '#444', marginBottom: '16px' }}>
                    Capture fingerprints to enable automatic biometric attendance.
                  </p>
                  <FingerprintCapture
                    capturedTemplates={fingerprintTemplates}
                    onCapture={handleFingerprintCapture}
                    disabled={saving}
                  />
                  <Divider style={{ margin: '16px 0' }} />
                </div>

                <Row gutter={[16, 0]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="name"
                      label="Full Name"
                      rules={[
                        { required: true, message: 'Please enter full name' },
                        { min: 2, message: 'Name must be at least 2 characters' }
                      ]}
                    >
                      <Input
                        prefix={<UserOutlined />}
                        placeholder="Enter full name"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="email"
                      label="Email Address"
                      rules={[
                        { type: 'email', message: 'Please enter valid email address' }
                      ]}
                    >
                      <Input placeholder="Enter email address (optional)" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="phone"
                      label="Phone Number"
                    >
                      <Input placeholder="Enter phone number" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="date_of_birth"
                      label="Date of Birth"
                    >
                      <DatePicker
                        style={{ width: '100%' }}
                        format="YYYY-MM-DD"
                        placeholder="Select date of birth"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="gender"
                      label="Gender"
                    >
                      <Select placeholder="Select gender">
                        <Option value="male">Male</Option>
                        <Option value="female">Female</Option>
                        <Option value="other">Other</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="added_via"
                      label="Added Via"
                    >
                      <Select placeholder="Select how consumer was added" allowClear>
                        <Option value="prime_time">Prime Time</Option>
                        <Option value="physical">Physical</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="address"
                      label="Address"
                    >
                      <TextArea
                        rows={3}
                        placeholder="Enter address"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </Col>

            {/* Account Information */}
            <Col span={24}>
              <div className="form-section">
                <Title level={4}>Account Information</Title>
                {!isEditing && (
                  <div style={{
                    backgroundColor: '#e6f7ff',
                    border: '1px solid #91d5ff',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '16px'
                  }}>
                    <strong>🔐 Automatic Account Setup:</strong>
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                      <li>A secure password will be automatically generated</li>
                      <li>Login credentials will be sent to the consumer's email</li>
                      <li>Consumer can change their password after first login</li>
                    </ul>
                  </div>
                )}
                <Row gutter={[16, 0]}>
                  {user?.role === 'super_admin' && (
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="wellness_center_id"
                        label="Wellness Center"
                        rules={[
                          { required: true, message: 'Please select wellness center' }
                        ]}
                      >
                        <Select placeholder="Select wellness center">
                          {centers.map(center => (
                            <Option key={center.id} value={center.id}>
                              {center.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  )}
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="start_date"
                      label="Start Date"
                      rules={[
                        { required: true, message: 'Please select start date' }
                      ]}
                    >
                      <DatePicker
                        style={{ width: '100%' }}
                        format="YYYY-MM-DD"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="is_active"
                      label="Active Status"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>


          <Divider />

          <div className="form-actions">
            <Space>
              <Button onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={saving}
                icon={<SaveOutlined />}
              >
                {isEditing ? 'Update Consumer' : 'Create Consumer'}
              </Button>
            </Space>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default ConsumerForm;
